import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import Button from "./ds/Button";
import Date from "./ds/Date";
import Icon from "./ds/Icon";
import Modal from "./ds/Modal";
import Overlay from "./ds/Overlay";
import Select from "./ds/Select";
import Textfield from "./ds/Textfield";
import Toggle from "./ds/Toggle";
import { postState, tagState, categoryState } from '../constants';
import { categoryService } from '../container';
import { useGlobalState } from '../state';

const orderByOptions = [
  {
    key: 'created_at_desc',
    label: 'Fecha de creación',
  },
  {
    key: 'updated_at_desc',
    label: 'Fecha de modificación',
  },
  {
    key: 'published_at_desc',
    label: 'Fecha de publicación',
  },
]

export default function Search() {
  const location = useLocation();
  const navigate = useNavigate();
  const { media } = useGlobalState();
  const [filter, setFilter] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [categories, setCategories] = useState([]);

  const [postFilters, setPostFilters] = useState({});
  const [tagFilters, setTagFilters] = useState({});
  const [categoryFilters, setCategoryFilters] = useState({});
  const [userFilters, setUserFilters] = useState({});


  // Filter type
  useEffect(() => {
    const path = location.pathname

    switch (path) {
      case '/':
        setFilterType('posts');
        return;
      case '/tags':
        setFilterType('tags');
        return;
      case '/sections':
        setFilterType('sections');
        return;
      case '/users':
        setFilterType('users');
        return;
      default:
        setFilterType('');
        return;
    }
  }, [location]);

  useEffect(() => {
    (async () => {
      try {
        const categories = await categoryService.search({
          media_id: media.id,
        });

        setCategories(categories);
      } catch (err) {
        toast.error('Ocurrió un error al cargar las categorías.');
      }
    })();
  }, []);

  const cleanFilters = () => {
    setPostFilters({});
    setTagFilters({});
    setCategoryFilters({});
    setUserFilters({});
  };

  const search = () => {
    if (filterType === 'posts') {
      const searchParams = new URLSearchParams();

      if (postFilters.title) {
        searchParams.append('title', postFilters.title);
      }

      if (postFilters.category_id) {
        searchParams.append('category_id', postFilters.category_id);
      }

      if (postFilters.state) {
        searchParams.append('state', postFilters.state);
      }

      if (postFilters.date_from) {
        // Convert to UTC ISO 8601 with dayjs
        const date = dayjs(postFilters.date_from, 'DD/MM/YYYY').startOf('day');
        searchParams.append('date_from', date.format());
      }

      if (postFilters.date_to) {
        // Convert to UTC ISO 8601 with dayjs
        const date = dayjs(postFilters.date_to, 'DD/MM/YYYY').endOf('day');
        searchParams.append('date_to', date.format());
      }

      if (postFilters.order_by) {
        searchParams.append('order_by', postFilters.order_by);
      }

      navigate({
        pathname: '/',
        search: `?${searchParams.toString()}`,
      });
    }

    if (filterType === 'tags') {
      const searchParams = new URLSearchParams();

      if (tagFilters.title) {
        searchParams.append('title', tagFilters.title);
      }

      if (tagFilters.state) {
        searchParams.append('state', tagFilters.state);
      }

      navigate({
        pathname: '/tags',
        search: `?${searchParams.toString()}`,
      });
    }

    if (filterType === 'sections') {
      const searchParams = new URLSearchParams();

      if (categoryFilters.title) {
        searchParams.append('title', categoryFilters.title);
      }

      if (categoryFilters.state) {
        searchParams.append('state', categoryFilters.state);
      }

      navigate({
        pathname: '/sections',
        search: `?${searchParams.toString()}`,
      });
    }

    if (filterType === 'users') {
      const searchParams = new URLSearchParams();

      if (userFilters.name) {
        searchParams.append('name', userFilters.name);
      }

      if (userFilters.role) {
        searchParams.append('role', userFilters.role);
      }

      navigate({
        pathname: '/users',
        search: `?${searchParams.toString()}`,
      });
    }

    setFilter(false);
  };

  const titleSearch = filterType === 'posts'
    ? postFilters.title
    : filterType === 'tags'
      ? tagFilters.title
      : filterType === 'sections'
        ? categoryFilters.title
        : filterType === 'users'
        ? userFilters.name
        : '';

  return (
    <>
      <form id="search" onSubmit={(e) => {
        e.preventDefault();
        search();
      }}>
        <Icon icon="search" />

        <input
          type="text"
          value={titleSearch || ''}
          onChange={(e) => {
            if (filterType === 'posts') {
              setPostFilters((postFilters) => ({ ...postFilters, title: e.target.value }));
              return;
            }

            if (filterType === 'tags') {
              setTagFilters((tagFilters) => ({ ...tagFilters, title: e.target.value }));
              return;
            }

            if (filterType === 'sections') {
              setCategoryFilters((categoryFilters) => ({ ...categoryFilters, title: e.target.value }));
              return;
            }

            if (filterType === 'users') {
              setUserFilters((userFilters) => ({ ...userFilters, name: e.target.value }));
              return;
            }
          }}
          placeholder="Buscar..."
        />

        <button type="button" id="filter" onClick={() => setFilter(true)}>
          <Icon icon="filter" />
        </button>
      </form>

      {filter && (
        <Overlay>
          <Modal title="Búsqueda avanzada" onclose={() => setFilter(false)}>
            <div id="filters">
              <Toggle>
                <li onClick={() => setFilterType('posts')} className={filterType == 'posts' ? 'selected' : ''}>Post</li>
                <li onClick={() => setFilterType('tags')} className={filterType == 'tags' ? 'selected' : ''}>Tag</li>
                <li onClick={() => setFilterType('sections')} className={filterType == 'sections' ? 'selected' : ''}>Sección</li>
                <li onClick={() => setFilterType('users')} className={filterType == 'users' ? 'selected' : ''}>Usuario</li>
              </Toggle>

              <div className="fields">
                {filterType === 'posts' && (
                  <>
                    <Textfield
                      placeholder="Título"
                      value={postFilters.title || ''}
                      onChange={(e) => {
                        setPostFilters((postFilters) => ({ ...postFilters, title: e.target.value}))
                      }}
                    />

                    <div className="wrapper">
                      <Select
                        placeholder="Sección"
                        value={postFilters.category_id}
                        onChange={(e) => {
                          setPostFilters((postFilters) => ({ ...postFilters, category_id: e.target.value}))
                        }}
                      >
                        {categories && categories.map((category) => (
                          <option key={category.id} value={category.id}>{category.title}</option>
                        ))}
                      </Select>

                      <Select
                        placeholder="Estado"
                        value={postFilters.state}
                        onChange={(e) => {
                          setPostFilters((postFilters) => ({ ...postFilters, state: e.target.value}))
                        }}
                      >
                        <option value={postState.published}>Publicado</option>
                        <option value={postState.draft}>Borrador</option>
                        <option value={postState.scheduled}>Programado</option>
                      </Select>
                    </div>

                    <div className="wrapper">
                      <Date
                        placeholder="Desde"
                        value={postFilters.date_from}
                        onChange={(date) => {
                          setPostFilters((postFilters) => ({ ...postFilters, date_from: date}));
                        }}
                      />
                      <Date
                        placeholder="Hasta"
                        value={postFilters.date_to}
                        onChange={(date) => {
                          setPostFilters((postFilters) => ({ ...postFilters, date_to: date}));
                        }}
                      />
                    </div>

                    <div className="wrapper">
                      <Select
                        placeholder="Ordernar por"
                        value={postFilters.order_by}
                        onChange={(e) => {
                          setPostFilters((postFilters) => ({ ...postFilters, order_by: e.target.value}))
                        }}
                      >
                        {orderByOptions.map((orderBy) => (
                          <option key={orderBy.key} value={orderBy.key}>{orderBy.label}</option>
                        ))}
                      </Select>
                    </div>
                  </>
                )}

                {filterType === 'tags' && (
                  <>
                    <Textfield
                      placeholder="Nombre"
                      value={tagFilters.title || ''}
                      onChange={(e) => {
                        setTagFilters((tagFilters) => ({ ...tagFilters, title: e.target.value}))
                      }}
                    />

                    <Select
                      placeholder="Estado"
                      value={tagFilters.state}
                      onChange={(e) => {
                        setTagFilters((tagFilters) => ({ ...tagFilters, state: e.target.value}))
                      }}
                    >
                      <option value={tagState.approved}>Aprobado</option>
                      <option value={tagState.pending}>Pendiente</option>
                      <option value={tagState.rejected}>Rechazado</option>
                    </Select>
                  </>
                )}

                {filterType === 'sections' && (
                  <>
                    <Textfield
                      placeholder="Nombre"
                      value={categoryFilters.title || ''}
                      onChange={(e) => {
                        setCategoryFilters((categoryFilters) => ({ ...categoryFilters, title: e.target.value}))
                      }}
                    />

                    <Select
                      placeholder="Estado"
                      value={categoryFilters.state}
                      onChange={(e) => {
                        setCategoryFilters((categoryFilters) => ({ ...categoryFilters, state: e.target.value}))
                      }}
                    >
                      <option value={categoryState.active}>Publicada</option>
                      <option value={categoryState.inactive}>Despublicada</option>
                    </Select>
                  </>
                )}

                {filterType === 'users' && (
                  <>
                    <Textfield
                      placeholder="Usuario"
                      value={userFilters.name || ''}
                      onChange={(e) => {
                        setUserFilters((userFilters) => ({ ...userFilters, name: e.target.value}))
                      }}
                    />

                    <Select
                      placeholder="Rol"
                      value={userFilters.role}
                      onChange={(e) => {
                        setUserFilters((userFilters) => ({ ...userFilters, role: e.target.value}))
                      }}
                    >
                      <option value="user">Periodista</option>
                      <option value="director">Director</option>
                      <option value="admin">Administrador</option>
                    </Select>
                  </>
                )}
              </div>
            </div>

            <div className="actions">
              <Button label="Buscar" onclick={search} />
              <Button label="Limpiar" type="secondary" onclick={cleanFilters} />
            </div>
          </Modal>
        </Overlay>
      )}
    </>
  )
}
